/**
 * Phase 3.26 — Geteilte Helfer für die in /components/dealer/ extrahierten
 * Settings-Sektionen.
 */
import React from "react";
import api from "../../lib/api";

/**
 * Jun-2026 (User-Wunsch, Etappe 2) — geteilter Hook + Select zur Verknüpfung
 * von Einstellungen mit einem Mitarbeiter (z. B. Ansprechpartner Werkstatt,
 * Geschäftsführer/Inhaber-Ansprechpartner).
 */
export function useEmployees() {
    const [employees, setEmployees] = React.useState([]);
    React.useEffect(() => {
        let alive = true;
        api.get("/dealer/employees")
            .then((r) => { if (alive) setEmployees(r.data?.items || []); })
            .catch(() => { if (alive) setEmployees([]); });
        return () => { alive = false; };
    }, []);
    return employees;
}

const empName = (e) => e?.name || `${e?.first_name || ""} ${e?.last_name || ""}`.trim() || e?.email || "Mitarbeiter";

export function EmployeeSelect({ employees, value, onSelect, placeholder = "Mitarbeiter wählen…", testId, disabled }) {
    return (
        <select
            value={value || ""}
            disabled={disabled}
            onChange={(e) => {
                const emp = employees.find((x) => x.id === e.target.value) || null;
                onSelect(e.target.value || null, emp);
            }}
            className="rounded-sm h-9 border border-gray-300 px-2 text-sm bg-white w-full disabled:bg-gray-100"
            data-testid={testId}
        >
            <option value="">{placeholder}</option>
            {employees.map((e) => (
                <option key={e.id} value={e.id}>
                    {empName(e)}{e.site_code ? ` · ${e.site_code}` : ""}{e.site_street ? ` · ${e.site_street}` : ""}
                </option>
            ))}
        </select>
    );
}

export { empName };

export function CheckRow({ label, hint, checked, disabled, onChange, color = "#FFCC00", testId }) {
    return (
        <label className="flex items-start gap-3 cursor-pointer py-2" data-testid={testId}>
            <input
                type="checkbox"
                checked={!!checked}
                disabled={disabled}
                onChange={(e) => onChange(e.target.checked)}
                className="mt-1 w-5 h-5"
                style={{ accentColor: color }}
            />
            <div className="flex-1">
                <div className="font-bold text-[#111827] text-sm">{label}</div>
                {hint && <div className="text-xs text-gray-600 mt-0.5">{hint}</div>}
            </div>
        </label>
    );
}

export function CollapsibleServerToggle({ enabled, onToggle, title, hint, children, testId }) {
    return (
        <div
            className={`border rounded-sm transition-colors ${enabled ? "border-[#2563EB] bg-blue-50/40" : "border-gray-200 bg-white"}`}
            data-testid={`server-toggle-${testId}`}
        >
            <label className="flex items-start gap-3 p-3 cursor-pointer">
                <input
                    type="checkbox"
                    checked={enabled}
                    onChange={(e) => onToggle(e.target.checked)}
                    className="mt-0.5 w-4 h-4 accent-[#2563EB]"
                    data-testid={`server-toggle-${testId}-checkbox`}
                />
                <div className="flex-1 min-w-0">
                    <div className="text-sm font-bold text-[#111827]">{title}</div>
                    <div className="text-[11px] text-gray-500 mt-0.5">{hint}</div>
                </div>
                <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-sm ${enabled ? "bg-[#2563EB] text-white" : "bg-gray-200 text-gray-600"}`}>
                    {enabled ? "Aktiv" : "Aus"}
                </span>
            </label>
            {enabled && (
                <div className="px-3 pb-3 pt-1 border-t border-blue-100" data-testid={`server-toggle-${testId}-body`}>
                    {children}
                </div>
            )}
        </div>
    );
}


/**
 * Einklappbare Karte mit eigenem Header (Jun 2026, User-Wunsch) — für die
 * Unterabschnitte im Bereich „Import / Export / Schnittstellen".
 */
export function CollapsibleCard({ icon: Icon, iconClass = "text-[#2563EB]", title, hint, badge, defaultOpen = false, testId, children }) {
    const [open, setOpen] = React.useState(defaultOpen);
    return (
        <div className="bg-white border border-gray-200 rounded-sm overflow-hidden" data-testid={testId}>
            <button
                type="button"
                onClick={() => setOpen((v) => !v)}
                className="w-full flex items-center gap-3 px-6 py-4 text-left hover:bg-gray-50 transition-colors"
                data-testid={testId ? `${testId}-toggle` : undefined}
                aria-expanded={open}
            >
                {Icon && <Icon className={`w-5 h-5 shrink-0 ${iconClass}`} />}
                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                        <h3 className="font-display font-bold text-lg">{title}</h3>
                        {badge}
                    </div>
                    {hint && <p className="text-xs text-gray-600 mt-0.5">{hint}</p>}
                </div>
                <span className={`text-gray-400 text-lg transition-transform shrink-0 ${open ? "rotate-180" : ""}`} aria-hidden="true">⌄</span>
            </button>
            {open && (
                <div className="px-6 pb-6" data-testid={testId ? `${testId}-body` : undefined}>
                    {children}
                </div>
            )}
        </div>
    );
}
